<template>
  <app-cell
    class="comment-cell"
    :class="{ 'comment-cell--deleted': isDeleted }"
    :edit="false"
    :deletable="false"
    :loading="loading"
    :editable="false"
  >
    <template slot="content">
      <div class="comment-cell__content">
        <div class="comment-cell__header">
          <div class="comment-cell__meta">
            <router-link
              v-if="comment.userProfile"
              :to="{ name: 'user', params: { id: comment.userProfile.id } }"
              class="comment-cell__user comment-cell__user--link"
            >
              {{ comment.userProfile.username }}
            </router-link>
            <span class="comment-cell__date">{{ formatDate(comment.createdAt) }}</span>
          </div>
          <div v-if="chatTarget" class="comment-cell__target">
            <span class="comment-cell__target-type">{{ chatTarget.__typename }}</span>
            <span class="comment-cell__target-info">{{ targetDisplayText }}</span>
          </div>
        </div>
        <div class="comment-cell__body">{{ comment.body }}</div>
      </div>
    </template>
    <template slot="actions">
      <base-button v-if="isDeleted" :disabled="true">Deleted</base-button>
      <base-button v-else :disabled="loading" @click="onDelete" :danger="true">Delete</base-button>
    </template>
  </app-cell>
</template>

<script lang="ts">
import AppCell from '@/components/Cell.vue';
import gql from 'graphql-tag';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ChatComment, ChatTarget } from '../types/api';

@Component({
  components: { AppCell },
})
export default class UserCommentCell extends Vue {
  @Prop({ required: true })
  comment!: ChatComment;

  loading = false;

  get chatTarget(): ChatTarget | null {
    return this.comment.chat.target || null;
  }

  get isDeleted(): boolean {
    return !!this.comment.deletedAt;
  }

  get targetDisplayText(): string {
    if (!this.chatTarget) return '';

    if (this.chatTarget.__typename === 'Blog') {
      return this.chatTarget.title;
    } else if (this.chatTarget.__typename === 'Show') {
      const venue = this.chatTarget.address?.venue || 'Unknown venue';
      const city = this.chatTarget.address?.city || 'Unknown city';
      const date = new Date(this.chatTarget.showDate).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      return `${venue}, ${city} (${date})`;
    }

    return '';
  }

  public formatDate(date: string) {
    return new Date(date).toLocaleString();
  }

  public async onDelete() {
    this.loading = true;
    try {
      await this.$apollo.mutate({
        mutation: gql`
          mutation DeleteChatComment($input: DeleteChatCommentInput!) {
            deleteChatComment(input: $input) {
              success
            }
          }
        `,
        variables: { input: { id: this.comment.id } },
        refetchQueries: ['User', 'AllComments'],
        awaitRefetchQueries: true,
      });
    } finally {
      this.loading = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.comment-cell {
  &__content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__user {
    font-weight: bold;
    color: $color-theme-red;

    &--link {
      text-decoration: none;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
        opacity: 0.8;
      }
    }
  }

  &__date {
    color: $color-disabled;
    font-size: 0.9em;
  }

  &__body {
    font-size: 1rem;
    white-space: pre-wrap;
  }

  &__target {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__target-type {
    font-weight: bold;
    color: $color-theme-red;
    font-size: 0.9em;
  }

  &__target-info {
    color: $color-disabled;
    font-size: 0.9em;
  }

  &--deleted {
    opacity: 0.6;
  }
}
</style>
